import { useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, ChevronRight, X, SlidersHorizontal } from "lucide-react";
import { CATEGORIES, PRODUCTS } from "@/data";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

export default function Products() {
  const headerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: headerRef, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const activeCategory = searchParams.get("category") || "";

  const categories = CATEGORIES;
  const products = PRODUCTS.filter(p =>
    (!activeCategory || p.category === activeCategory) &&
    (!search || p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.shortDescription.toLowerCase().includes(search.toLowerCase()))
  );

  const handleCategoryClick = (catId) => {
    const params = new URLSearchParams(searchParams);
    if (catId === activeCategory) {
      params.delete("category");
    } else {
      params.set("category", catId);
    }
    setSearchParams(params);
  };

  const handleSearch = (value) => {
    setSearch(value);
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set("search", value);
    } else {
      params.delete("search");
    }
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearch("");
    setSearchParams({});
  };

  const activeCategoryName = categories.find(c => c.id === activeCategory)?.name;

  return (
    <div className="pt-20" data-testid="products-page">
      {/* Header */}
      <section ref={headerRef} className="py-12 md:py-16 bg-[#f8f8f8] relative overflow-hidden">
        <motion.img
          src="https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=1600&q=80"
          alt=""
          aria-hidden="true"
          style={{ y: imageY }}
          className="absolute inset-0 w-full h-[140%] object-cover opacity-[0.22] scale-105"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <Badge className="bg-green-100 text-[#2B7A1E] hover:bg-green-100 mb-4 px-4 py-1.5 text-xs font-semibold tracking-wide rounded-full border-0">
            Our Products
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#333] tracking-tight mb-3" style={{ fontFamily: 'Montserrat' }}>
            {activeCategoryName || "All Products"}
          </h1>
          <p className="text-gray-600 max-w-xl">
            Browse our complete range of premium frozen fruits, pulps, vegetables, and specialty products.
          </p>
        </div>
      </section>

      <section className="py-10 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          {/* Filters Row */}
          <div className="flex flex-col md:flex-row gap-4 mb-10">
            {/* Search */}
            <div className="flex items-center flex-1 max-w-md h-11 px-4 gap-2 rounded-full border border-gray-200 bg-[#f8f8f8] focus-within:bg-white focus-within:border-[#2B7A1E] transition-colors">
              <Search size={18} className="text-gray-400 shrink-0" />
              <input
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search products..."
                className="flex-1 bg-transparent outline-none text-sm text-gray-700 placeholder:text-gray-400"
                data-testid="product-search-input"
              />
              {search && (
                <button onClick={() => handleSearch("")} className="text-gray-400 hover:text-gray-600 shrink-0">
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Category Pills */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleCategoryClick("")}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${!activeCategory ? "bg-[#2B7A1E] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                data-testid="filter-all"
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryClick(cat.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${activeCategory === cat.id ? "bg-[#2B7A1E] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  data-testid={`filter-${cat.id}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Active Filters */}
          {(activeCategory || search) && (
            <div className="flex items-center gap-3 mb-6">
              <SlidersHorizontal size={14} className="text-gray-400" />
              <span className="text-sm text-gray-500">Active filters:</span>
              {activeCategory && (
                <Badge className="bg-green-50 text-[#2B7A1E] hover:bg-green-100 border-0 gap-1 cursor-pointer" onClick={() => handleCategoryClick("")}>
                  {activeCategoryName} <X size={12} />
                </Badge>
              )}
              {search && (
                <Badge className="bg-blue-50 text-[#005A80] hover:bg-blue-100 border-0 gap-1 cursor-pointer" onClick={() => handleSearch("")}>
                  "{search}" <X size={12} />
                </Badge>
              )}
              <button onClick={clearFilters} className="text-xs text-gray-400 hover:text-gray-600 underline" data-testid="clear-filters-btn">
                Clear all
              </button>
            </div>
          )}

          {/* Results count */}
          <p className="text-sm text-gray-500 mb-6" data-testid="products-count">
            Showing {products.length} product{products.length !== 1 ? "s" : ""}
          </p>

          {/* Product Grid */}
          {products.length === 0 ? (
            <div className="text-center py-20" data-testid="no-products-found">
              <p className="text-lg font-semibold text-gray-400 mb-2" style={{ fontFamily: 'Montserrat' }}>No products found</p>
              <p className="text-sm text-gray-400 mb-4">Try adjusting your search or filter criteria.</p>
              <Button onClick={clearFilters} variant="outline" className="rounded-full" data-testid="reset-filters-btn">
                Reset Filters
              </Button>
            </div>
          ) : (
            <motion.div
              key={activeCategory + "|" + search}
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {products.map((product) => (
                <motion.div key={product.id} variants={fadeUp}>
                  <Link to={`/products/${product.id}`} data-testid={`product-card-${product.id}`}>
                    <div className="group bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                      <div className="aspect-square product-img-zoom relative">
                        <img src={product.image} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
                        {product.featured && (
                          <Badge className="absolute top-3 left-3 bg-[#D94F04] text-white border-0 text-xs">Featured</Badge>
                        )}
                      </div>
                      <div className="p-5">
                        <span className="text-xs text-[#2B7A1E] font-semibold uppercase tracking-wide">{product.categoryName}</span>
                        <h3 className="text-base font-bold text-[#333] mt-1 group-hover:text-[#2B7A1E] transition-colors line-clamp-1" style={{ fontFamily: 'Montserrat' }}>
                          {product.name}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{product.shortDescription}</p>
                        <div className="flex items-center justify-between mt-4">
                          <span className="text-xs text-gray-400">{product.form}</span>
                          <span className="text-xs text-[#2B7A1E] font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                            Details <ChevronRight size={12} />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
